import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Menu, X } from "lucide-react";

// ---------------------------------------------------------------------------
// Navigation items — active items link to real demo pages,
// Coming Soon items link to /hub/coming-soon/:section.
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  icon: string;
  href: string;
  active: boolean;
  testId: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard",        icon: "◧",  href: "/hub/dashboard-demo",           active: true,  testId: "nav-hub-dashboard" },
  { label: "Tournaments",      icon: "🏆", href: "/hub/tournament-demo",           active: true,  testId: "nav-hub-tournaments" },
  { label: "Selection",        icon: "⚖️",  href: "/hub/selection-demo",            active: true,  testId: "nav-hub-selection" },
  { label: "Games & Schedule", icon: "📅", href: "/hub/games-schedule-demo",    active: true,  testId: "nav-hub-games" },
  { label: "Teams & Rosters",  icon: "👥", href: "/hub/teams-rosters-demo",     active: true,  testId: "nav-hub-teams" },
  { label: "Age Verification", icon: "✓",  href: "/hub/age-verification-demo",  active: true,  testId: "nav-hub-age" },
  { label: "Fitness Tests",    icon: "⚡", href: "/hub/fitness-tests-demo",     active: true,  testId: "nav-hub-fitness" },
  { label: "Performance Stats",icon: "📊", href: "/hub/performance-stats-demo", active: true,  testId: "nav-hub-performance" },
  { label: "Announcements",    icon: "📣", href: "/hub/announcements-demo",     active: true,  testId: "nav-hub-announcements" },
  { label: "Reports",          icon: "📈", href: "/hub/reports-demo",           active: true,  testId: "nav-hub-reports" },
  { label: "Grounds",          icon: "⌖",  href: "/hub/coming-soon/grounds",    active: false, testId: "nav-hub-grounds" },
];

const ACTIVE_HREFS = new Set(NAV_ITEMS.filter((n) => n.active).map((n) => n.href));

// ---------------------------------------------------------------------------

interface Props {
  children: React.ReactNode;
}

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      {/* Wordmark */}
      <div className="px-5 py-5 border-b border-slate-700">
        <div className="flex items-start justify-between gap-2">
          <Link
            to="/hub/dashboard-demo"
            onClick={onClose}
            className="block"
          >
            <p className="text-white font-bold text-base leading-tight">
              Hub Tournament
              <br />
              Manager
            </p>
          </Link>
          <span className="text-[10px] font-bold bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded shrink-0 mt-0.5">
            DEMO
          </span>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" aria-label="Hub navigation">
        {NAV_ITEMS.map((item, idx) => {
          const isCurrentPage = location.pathname === item.href;
          const isComingSoon = !item.active;

          // Divider before first Coming Soon item
          const prevActive = idx > 0 && NAV_ITEMS[idx - 1].active;
          const showDivider = isComingSoon && prevActive;

          return (
            <div key={item.href}>
              {showDivider && (
                <div className="mt-3 mb-2 px-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Coming Soon
                  </p>
                </div>
              )}
              <Link
                to={item.href}
                onClick={onClose}
                data-testid={item.testId}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors",
                  isCurrentPage
                    ? "bg-blue-600 text-white font-semibold"
                    : isComingSoon
                      ? "text-slate-500 hover:text-slate-400 hover:bg-slate-700/40 cursor-default pointer-events-none"
                      : "text-slate-300 hover:text-white hover:bg-slate-700 font-medium"
                )}
                aria-current={isCurrentPage ? "page" : undefined}
              >
                <span className="text-base w-5 text-center shrink-0">{item.icon}</span>
                <span className="flex-1 truncate">{item.label}</span>
                {isComingSoon && (
                  <span className="text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded shrink-0">
                    Soon
                  </span>
                )}
                {isCurrentPage && ACTIVE_HREFS.size > 0 && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0" />
                )}
              </Link>
            </div>
          );
        })}
      </nav>

      {/* Footer note */}
      <div className="px-5 py-4 border-t border-slate-700">
        <p className="text-xs text-slate-500">
          Sample data · Not connected to live systems
        </p>
      </div>
    </div>
  );
}

export function HubAdminDemoLayout({ children }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col" data-testid="hub-admin-layout">
      {/* DEMO banner — full width, always visible */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800 font-medium shrink-0 z-30 relative">
        DEMO — Sample data for product preview. Not connected to live systems.
      </div>

      {/* Below banner: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <aside
          className="hidden md:flex md:flex-col w-60 shrink-0 bg-slate-800 border-r border-slate-700"
          data-testid="hub-sidebar"
        >
          <SidebarContent />
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileOpen(false)}
            />
            {/* Drawer */}
            <div className="absolute left-0 top-0 bottom-0 w-60 bg-slate-800 border-r border-slate-700 z-50 flex flex-col">
              <div className="flex items-center justify-end px-4 py-3 border-b border-slate-700">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="text-slate-400 hover:text-white"
                  aria-label="Close sidebar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarContent onClose={() => setMobileOpen(false)} />
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto bg-gray-50">
          {/* Mobile hamburger bar */}
          <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-slate-800 border-b border-slate-700">
            <button
              onClick={() => setMobileOpen(true)}
              className="text-slate-300 hover:text-white"
              aria-label="Open sidebar"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <p className="text-white font-semibold text-sm">Hub Tournament Manager</p>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
